import { Route$MatchedEvent } from "sap/ui/core/routing/Route";
import JSONModel from "sap/ui/model/json/JSONModel";
import BaseController from "./BaseController";

/** Base class for controllers with filtered lists.
 * Manages resetting filters, fetching data, and keeps current filters in local storage */
export default class FilterController extends BaseController {
  protected _defaultFilters: Record<string, unknown>;
  private _dataEndpoint: string;
  private _filtersModel: JSONModel;
  private _resetEveryDay: boolean;

  protected initFilters(
    defaultFilters: unknown,
    dataEndpoint: string,
    resetEveryDay = false
  ) {
    this._defaultFilters = defaultFilters as Record<string, unknown>;
    this._dataEndpoint = dataEndpoint;
    this._resetEveryDay = resetEveryDay;

    let filters;
    const storedFilters = localStorage.getItem(this.getFiltersStorageKey());
    if (storedFilters) {
      filters = JSON.parse(storedFilters);
    } else {
      filters = JSON.parse(JSON.stringify(defaultFilters));
    }

    this._filtersModel = new JSONModel(filters);
    this._filtersModel.attachPropertyChange(() => this.updateStorageFilter());
    this.setModel(this._filtersModel, "filters");
  }

  protected getFiltersStorageKey() {
    const controllerName = this.getView()?.getControllerName();
    return `${controllerName}.filters`;
  }

  private resetFiltersModelIfNecessary(): void {
    const lastAppliedStorageKey = `${this.getFiltersStorageKey()}.dateApplied`;

    if (this._resetEveryDay) {
      const lastAppliedStr = localStorage.getItem(lastAppliedStorageKey);
      if (lastAppliedStr) {
        const lastAppliedDate = new Date(lastAppliedStr);
        if (lastAppliedDate.getDate() != new Date().getDate()) {
          this.resetFiltersModel();
        }
      }
    }

    localStorage.setItem(lastAppliedStorageKey, new Date().toJSON());
  }

  private updateStorageFilter() {
    const currentFilter = this._filtersModel.getData();
    localStorage.setItem(
      this.getFiltersStorageKey(),
      JSON.stringify(currentFilter)
    );
  }

  protected _onRouteMatched(oEvent: Route$MatchedEvent) {
    super._onRouteMatched(oEvent);
    this.applyFilters();
  }

  protected resetFiltersModel() {
    this._filtersModel.setData(
      JSON.parse(JSON.stringify(this._defaultFilters))
    ); // here we have to deep copy object, otherwise changing filters writes also to this._defaultFilters
    this.updateStorageFilter(); // setData does not trigger property change event
  }

  public async resetFilters() {
    this.resetFiltersModel();
    await this.applyFilters();
  }

  public async applyFilters() {
    this.resetFiltersModelIfNecessary();
    this.fetchData(this.getFiltersData());
  }

  protected async fetchData(filters: Record<string, unknown>) {
    await this.fetchModelPost(this._dataEndpoint, undefined, {
      body: this.buildRequestBody(filters),
    });
  }

  protected buildRequestBody(filters: Record<string, unknown>) {
    return filters;
  }

  protected getDataEndpoint() {
    return this._dataEndpoint.toString();
  }

  protected getFiltersData() {
    return this._filtersModel.getData();
  }
}
